import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { minimumAllowedNumberOfTacticsInGroup } from '../TacticForm.constants';
import { SelectOption } from 'basics/options/options.types';
import useTacticOptions from 'basics/options/tactic.options';
import { formValuesToTactic } from 'basics/transformers/Tactic.transformer';
import { CepStateType, TacticStateType } from 'basics/types/maya.types';
import { Channel, CustomerEngagementPlanning, Tactic } from 'generated/maya.types';
import cepState from 'states/cep.states';
import tacticState from 'states/tactic.states';
import { YodaValues } from 'yoda-ui/yodaForm/yodaForm.types';

const usePreviousTactics = (
  formValues: YodaValues,
  tacticGroupFieldValue: string | null | undefined,
  isOptionsWithChannelGroup: boolean,
) => {
  const flags = useFlags();
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;
  const cepId = cepStateValue.cep?._id || null;
  const tacticId = tacticStateValue.tactic?._id ? [tacticStateValue.tactic?._id] : [];
  const childChannels = Object.values(Channel).filter((value) => value);
  const tacticOptions: SelectOption[] = useTacticOptions(cepId, tacticId, childChannels);
  const [previousTacticOptions, setPreviousTacticOptions] = useState<SelectOption[]>();

  const hasIndirectCycleInGroups = (tactics: Tactic[]): boolean => {
    const parentMap = new Map(tactics.map((tactic) => [tactic._id, tactic.parentTactic?._id]));

    const prevToTacticMap = tactics.reduce((map, tactic) => {
      const prevId = tactic.previousTactic?._id;
      if (!prevId) return map;

      const updated = new Map(map);
      const existing = updated.get(prevId) ?? [];
      updated.set(prevId, [...existing, tactic._id]);
      return updated;
    }, new Map<string, string[]>());

    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const detectCycleFrom = (tacticItemId: string): boolean => {
      if (recursionStack.has(tacticItemId)) {
        return true;
      }

      if (visited.has(tacticItemId)) {
        return false;
      }

      visited.add(tacticItemId);
      recursionStack.add(tacticItemId);

      const nextNodes = prevToTacticMap.get(parentMap.get(tacticItemId) ?? '') ?? [];
      const result = nextNodes.some((childId) => detectCycleFrom(childId));

      recursionStack.delete(tacticItemId);
      return result;
    };

    return tactics.some((item) => detectCycleFrom(item._id));
  };

  const hasCycleInStandaloneTactics = (tactics: Tactic[]): boolean => {
    const tacticMap = new Map<string, string | undefined>();

    tactics.forEach((tactic) => {
      tacticMap.set(tactic._id, tactic.previousTactic?._id);
    });

    const visited = new Set<string>();
    const stack = new Set<string>();

    const dfs = (tacticItemId: string): boolean => {
      if (stack.has(tacticItemId)) return true;
      if (visited.has(tacticItemId)) return false;

      visited.add(tacticItemId);
      stack.add(tacticItemId);

      const prevId = tacticMap.get(tacticItemId);
      if (prevId && dfs(prevId)) return true;

      stack.delete(tacticItemId);
      return false;
    };

    return tactics.some((tactic) => dfs(tactic._id));
  };

  const filterByCycle = (options: SelectOption[]) => {
    let currentTactic = tacticStateValue.tactic;
    const currentCep: CustomerEngagementPlanning = cepStateValue.cep as CustomerEngagementPlanning;
    const { tactics } = cepStateValue;
    const groups = cepStateValue.tacticGroups;

    const parentTactic = [...tactics, ...groups].find((tactic) => formValues.tacticGroup === tactic._id);
    const filteredOptions: SelectOption[] = [];

    options.forEach((currentOption) => {
      const previousTactic = [...tactics, ...groups].find((tactic) => currentOption.id === tactic?._id);
      const allTactics = [...(cepStateValue.tactics ?? [])];

      if (!currentTactic) {
        currentTactic = formValuesToTactic(formValues, currentCep, parentTactic, previousTactic);
        allTactics.push(currentTactic);
      }

      const formattedTactics = allTactics.map((tactic) => {
        if (currentTactic?._id === tactic?._id) {
          return { ...tactic, previousTactic, parentTactic };
        }
        return tactic;
      });

      const isCycle = hasIndirectCycleInGroups(formattedTactics) || hasCycleInStandaloneTactics(formattedTactics);

      if (!isCycle) {
        filteredOptions.push(currentOption);
      }
    });

    return filteredOptions;
  };

  const filterOptionsByCorrectRelations = (options: SelectOption[]) => {
    const previousTacticId = tacticGroupFieldValue || formValues.tacticGroup;

    const filteredOptions = options.filter((tacticOption) => {
      const isCorrectTactic = !!(cepStateValue.tactics.find((tactic) => tactic._id === tacticOption.id && !tactic.parentTactic));
      const tacticsInGroup = cepStateValue?.tactics?.filter((tactic) => tactic.parentTactic?._id === tacticOption.id);
      const isCorrectGroup = tacticOption.id !== previousTacticId && tacticsInGroup.length >= minimumAllowedNumberOfTacticsInGroup;
      return isCorrectGroup || isCorrectTactic;
    });

    const filteredByCycle: SelectOption[] = filterByCycle(filteredOptions);
    return filteredByCycle;
  };

  const isStandaloneTactic = (): boolean => {
    if (tacticGroupFieldValue) {
      return false;
    }

    if (!tacticStateValue.tactic || !tacticStateValue.tactic?.parentTactic?._id) {
      return true;
    }

    const parentTacticId = tacticStateValue.tactic.parentTactic?._id;
    const currentGroup = cepStateValue.tacticGroups.find((group) => group._id === parentTacticId);

    if (currentGroup) {
      return false;
    }

    return false;
  };

  useEffect(() => {
    const tactics: Tactic[] = cepStateValue?.tactics;
    const groups: Tactic[] = cepStateValue?.tacticGroups;

    const filteredTacticOptions = tacticOptions.filter((tacticOption) => {
      if (flags.mayaBdcfcsd1585EnableTacticGroup && isOptionsWithChannelGroup) {
        return isStandaloneTactic()
          ? [...groups, ...tactics].find((tactic) => tactic._id === tacticOption.id)
          : [...groups].find((tactic) => tactic._id === tacticOption.id);
      }

      return tactics.find((tactic) => tactic._id === tacticOption.id);
    });

    if (flags.mayaBdcfcsd1585EnableTacticGroup && isOptionsWithChannelGroup) {
      setPreviousTacticOptions(filterOptionsByCorrectRelations(filteredTacticOptions));
    } else {
      setPreviousTacticOptions(filteredTacticOptions);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tacticGroupFieldValue, flags.mayaBdcfcsd1585EnableTacticGroup, isOptionsWithChannelGroup]);

  return {
    previousTacticOptions,
  };
};

export default usePreviousTactics;
